/**
 * Мастер заказа услуги по экологии (ecology-order; шаги — по файлу отдела экологии 28.09.2026,
 * ecology-order-steps): «Услуга → Площадки → Вопросы», затем экран «Заявка принята». Услуга — из
 * справочника экологии (только позиции, доступные клиенту в заказе); площадки — отмеченные клиентом,
 * новую можно добавить прямо в мастере; вопросы — «Вопросы при заказе» этой услуги из опросника,
 * ответы подставлены из опроса площадки. Шаг «Вопросы» есть только у услуги с такими вопросами
 * (у КЭР и ФГИС ОПВК в файле «заявка принята» — второй экран), отдельного шага «Проверка» нет —
 * состав заявки виден в карточке заказа. Комментарий менеджеру и «Отправить заявку» — на последнем
 * шаге.
 *
 * Шаг «Услуга» (ecology-order-service-search): заказ из подраздела показывает только услуги этого
 * подраздела (кнопку «Показать все услуги» можно нажать), иначе — все услуги группами как в меню
 * «Экологии» (абонентское обслуживание — своя группа, не отчётность); строка поиска — по названию и
 * описанию услуги.
 *
 * У услуги «все площадки отмечены заранее» (`orgWide`, абонентское обслуживание) при выборе
 * отмечаются все площадки организации — лишние клиент снимает.
 *
 * Поля заявки (ecology-order-fields): под каждой отмеченной площадкой — реквизиты услуги из
 * справочника («Кол-во источников выбросов», «Виды лицензируемой деятельности»), все по умолчанию
 * необязательные. Число, совпадающее по ключу с полем карточки строки этой площадки, подставляется
 * из карточки при отметке площадки или выборе услуги.
 *
 * Черновик сохраняется на сервере при переходе между шагами; подача проверяется сервером
 * авторитетно (услуга доступна, площадки свои) и замораживает «фотографию» заказа.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, Leaf, Plus, SearchX } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  ApiError,
  ecologyClientApi,
  ecologyOrdersApi,
  ordersApi,
  type EcoAnswers,
  type EcoFieldFile,
  type EcoFkkoEntry,
  type EcoCatalogItem,
  type EcoOrder,
  type EcoQuestion,
  type EcoSection,
  type EcoSite,
  type EcoSiteItem,
  type OrderDetail,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_SERVICE_GROUP_LABELS,
  ECO_SERVICE_GROUPS,
  ecoServiceGroup,
  ecoServiceMatches,
  ecoSiteCategoryLabel,
  ecoTermLabel,
} from '@/lib/ecology'
import { QuestionField } from '@/ecology/survey/question-field'
import { SiteDialog } from '@/ecology/site-dialog'
import { OrderSiteFields, type OrderSiteFieldValues } from './order-site-fields'
import { fkkoEntriesFrom, fkkoRowErrors, fkkoRowsFrom, type FkkoRow } from '@/ecology/fkko-list'
import { Card } from '@/design-system/components/layout/card'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Stepper } from '@/design-system/components/data/stepper'
import { ServicePickCard } from '@/design-system/components/data/service-pick-card'
import { LabeledCheckbox } from '@/sout/labeled-checkbox'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'

type StepKey = 'service' | 'sites' | 'questions'

const STEP_LABELS: Record<StepKey, string> = {
  service: 'Услуга',
  sites: 'Площадки',
  questions: 'Вопросы',
}

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

export function EcologyOrderWizard({
  order,
  ecoOrder,
  catalog,
  questions,
  sites: initialSites,
  items = [],
  section = null,
  onSubmitted,
}: {
  order: OrderDetail
  ecoOrder: EcoOrder
  /** Справочник услуг (для выбора; берутся только доступные клиенту в заказе). */
  catalog: EcoCatalogItem[]
  /** Включённые вопросы опросника. */
  questions: EcoQuestion[]
  /** Площадки организации (новые, добавленные в мастере, дописываются к ним). */
  sites: EcoSite[]
  /** Строки раздела организации — из их карточек подставляются числа полей заявки. */
  items?: EcoSiteItem[]
  /** Подраздел, из которого начат заказ: шаг «Услуга» показывает только его услуги. */
  section?: EcoSection | null
  onSubmitted: () => void
}) {
  const navigate = useNavigate()
  const [step, setStep] = useState<StepKey>(() =>
    // Услуга черновика может быть недоступна к заказу (позиция в архиве или снята галочка
    // «показывать в заказе») — такой мастер начинается с выбора услуги, иначе клиент получил бы
    // отказ при подаче.
    catalog.some((c) => c.id === ecoOrder.catalogItemId && c.showInOrder) ? 'sites' : 'service',
  )
  const [catalogItemId, setCatalogItemId] = useState(ecoOrder.catalogItemId)
  const [sites, setSites] = useState<EcoSite[]>(initialSites)
  const [siteIds, setSiteIds] = useState<string[]>(
    ecoOrder.sites.map((s) => s.siteId).filter(Boolean),
  )
  // Площадки отмечены автоматически (услуга «все площадки отмечены заранее») и клиент их не трогал —
  // при смене услуги на обычную такая отметка снимается.
  const [autoSites, setAutoSites] = useState(() =>
    catalog.some((c) => c.id === ecoOrder.catalogItemId && c.orgWide),
  )
  const [siteDialogOpen, setSiteDialogOpen] = useState(false)
  // Поля заявки по площадкам (id площадки → ключ поля → значение формы) и ошибки сервера по ним
  // («<id площадки>.<ключ>»).
  const [siteValues, setSiteValues] = useState<Record<string, OrderSiteFieldValues>>(() =>
    Object.fromEntries(
      ecoOrder.sites
        .filter((s) => s.siteId)
        .map((s) => [
          s.siteId,
          Object.fromEntries(
            Object.entries(s.values).map(([key, v]) => [key, Array.isArray(v) ? v : String(v)]),
          ),
        ]),
    ),
  )
  // Отходы полей «список ФККО» и файлы файловых полей по площадкам (ecology-order-fields-files-fkko).
  const [siteFkko, setSiteFkko] = useState<Record<string, Record<string, FkkoRow[]>>>(() =>
    Object.fromEntries(
      ecoOrder.sites
        .filter((s) => s.siteId)
        .map((s) => [
          s.siteId,
          Object.fromEntries(
            Object.entries(s.fkko).map(([key, list]) => [key, fkkoRowsFrom(list)]),
          ),
        ]),
    ),
  )
  const [siteFiles, setSiteFiles] = useState<Record<string, Record<string, EcoFieldFile[]>>>(() =>
    Object.fromEntries(ecoOrder.sites.filter((s) => s.siteId).map((s) => [s.siteId, s.files])),
  )
  const [filesBusy, setFilesBusy] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [answers, setAnswers] = useState<EcoAnswers>(ecoOrder.answers)
  const [comment, setComment] = useState(ecoOrder.comment)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState<OrderDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [serviceSection, setServiceSection] = useState<EcoSection | null>(section)
  const [serviceQuery, setServiceQuery] = useState('')
  const topRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    topRef.current?.scrollIntoView?.({ block: 'start' })
  }, [step, sent])

  const orderable = useMemo(() => catalog.filter((c) => c.showInOrder), [catalog])
  const item = orderable.find((c) => c.id === catalogItemId) ?? null
  // Шаг «Услуга»: группы по порядку меню, в каждой — услуги под строку поиска; пустые не показываем.
  const serviceGroups = useMemo(
    () =>
      (serviceSection ? [serviceSection] : ECO_SERVICE_GROUPS)
        .map((group) => ({
          group,
          items: orderable.filter(
            (c) => ecoServiceGroup(c) === group && ecoServiceMatches(c, serviceQuery),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [orderable, serviceSection, serviceQuery],
  )
  const orderQuestions = useMemo(() => {
    const ids = item?.orderQuestionIds ?? []
    return ids.map((id) => questions.find((q) => q.id === id)).filter((q): q is EcoQuestion => !!q)
  }, [item, questions])
  // Шаг «Вопросы» — только у услуги с вопросами при заказе; последний шаг — с комментарием и подачей.
  const steps = useMemo<StepKey[]>(
    () => (orderQuestions.length > 0 ? ['service', 'sites', 'questions'] : ['service', 'sites']),
    [orderQuestions.length],
  )
  const stepIndex = Math.max(steps.indexOf(step), 0)
  const lastStep = steps[steps.length - 1]
  const orderFields = item?.orderFields ?? []

  /**
   * Поля заявки площадки с подстановкой из карточки строки: пустое число берётся из поля карточки
   * с тем же ключом у строки этой площадки по услуге `service` (если внесено).
   */
  function withCardValues(
    current: OrderSiteFieldValues,
    siteId: string,
    service: EcoCatalogItem,
  ): OrderSiteFieldValues {
    const row = items.find((i) => i.siteId === siteId && i.catalogItemId === service.id)
    if (!row) return current
    const next = { ...current }
    for (const f of service.orderFields) {
      const card = row.values[f.key]
      if (f.type === 'number' && !next[f.key] && card) next[f.key] = card
    }
    return next
  }

  // Шаг пропал (у выбранной услуги нет вопросов при заказе) — на последний.
  useEffect(() => {
    if (!steps.includes(step)) setStep(lastStep)
  }, [steps, step, lastStep])

  /** Отходы площадки с подстановкой из поля «Отходы по ФККО» карточки строки паспортов этой площадки. */
  function withCardFkko(
    current: Record<string, FkkoRow[]>,
    siteId: string,
    service: EcoCatalogItem,
  ): Record<string, FkkoRow[]> {
    const row = items.find((i) => i.siteId === siteId && i.catalogItemId === service.id)
    if (!row) return current
    const next = { ...current }
    for (const f of service.orderFields) {
      const card = row.fkko?.[f.key]
      if (f.type === 'fkko_list' && !next[f.key]?.length && card?.length)
        next[f.key] = fkkoRowsFrom(card)
    }
    return next
  }

  /**
   * Выбор услуги: у услуги «все площадки отмечены заранее» пустой выбор площадок заполняется; у
   * обычной услуги автоматическая отметка (клиент её не менял) снимается.
   */
  function chooseService(next: EcoCatalogItem) {
    let nextSiteIds = siteIds
    if (next.orgWide && siteIds.length === 0) {
      nextSiteIds = sites.map((s) => s.id)
      setAutoSites(true)
    } else if (!next.orgWide && autoSites) {
      nextSiteIds = []
      setAutoSites(false)
    }
    setSiteIds(nextSiteIds)
    // У другой услуги свои поля заявки (прежние значения сервер тоже удаляет) — заново, с
    // подстановкой из карточек.
    if (next.id !== catalogItemId) {
      setSiteValues(Object.fromEntries(nextSiteIds.map((id) => [id, withCardValues({}, id, next)])))
      setSiteFkko(Object.fromEntries(nextSiteIds.map((id) => [id, withCardFkko({}, id, next)])))
      setSiteFiles({})
      setFieldErrors({})
    }
    setCatalogItemId(next.id)
    setDirty(true)
  }

  /** Площадку отметили или сняли; отмеченной подставляются числа из карточки. */
  function toggleSite(siteId: string, on: boolean) {
    setSiteIds((prev) => (on ? [...prev, siteId] : prev.filter((id) => id !== siteId)))
    if (on && item) {
      setSiteValues((prev) => ({
        ...prev,
        [siteId]: withCardValues(prev[siteId] ?? {}, siteId, item),
      }))
      setSiteFkko((prev) => ({ ...prev, [siteId]: withCardFkko(prev[siteId] ?? {}, siteId, item) }))
    }
    setAutoSites(false)
    setDirty(true)
  }

  function changeSiteValue(siteId: string, key: string, value: string | string[]) {
    setSiteValues((prev) => ({ ...prev, [siteId]: { ...prev[siteId], [key]: value } }))
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next[`${siteId}.${key}`]
      return next
    })
    setDirty(true)
  }

  function changeSiteFkko(siteId: string, key: string, rows: FkkoRow[]) {
    setSiteFkko((prev) => ({ ...prev, [siteId]: { ...prev[siteId], [key]: rows } }))
    setFieldErrors((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`${siteId}.${key}.`))),
    )
    setDirty(true)
  }

  /**
   * Поля заявки для сервера — по отмеченным площадкам и полям услуги, незаполненные — пустыми
   * (сервер их очищает). Файлы уходят отдельными запросами — здесь их нет.
   */
  function siteValuesPayload(): Record<string, Record<string, string | string[] | EcoFkkoEntry[]>> {
    return Object.fromEntries(
      siteIds.map((id) => [
        id,
        Object.fromEntries(
          orderFields
            .filter((f) => f.type !== 'file_multi')
            .map((f) => {
              if (f.type === 'fkko_list')
                return [f.key, fkkoEntriesFrom(siteFkko[id]?.[f.key] ?? [])]
              const v = siteValues[id]?.[f.key]
              if (f.type === 'number') return [f.key, typeof v === 'string' ? v.trim() : '']
              return [f.key, Array.isArray(v) ? v : []]
            }),
        ),
      ]),
    )
  }

  /** Файлы площадок — из ответа сервера (у заказа только что прикрепили или открепили файл). */
  function filesFrom(next: EcoOrder) {
    setSiteFiles(
      Object.fromEntries(next.sites.filter((s) => s.siteId).map((s) => [s.siteId, s.files])),
    )
  }

  /**
   * Файл поля заявки уходит сразу: сначала черновик сохраняется (площадка должна быть в заказе на
   * сервере), потом — сам файл. Ошибка — над шагом, поле остаётся доступным.
   */
  async function uploadFile(siteId: string, key: string, file: File) {
    setFilesBusy(true)
    setError(null)
    try {
      if (!(await saveDraft())) return
      filesFrom(await ecologyOrdersApi.uploadFieldFile(order.id, siteId, key, file))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось прикрепить файл'))
    } finally {
      setFilesBusy(false)
    }
  }

  async function deleteFile(siteId: string, key: string, fileId: string) {
    setFilesBusy(true)
    setError(null)
    try {
      filesFrom(await ecologyOrdersApi.deleteFieldFile(order.id, siteId, key, fileId))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить файл'))
    } finally {
      setFilesBusy(false)
    }
  }

  /** Площадка добавлена в мастере: берём её из свежего обзора и сразу отмечаем. */
  function siteAdded(fresh: EcoSite[]) {
    const known = new Set(sites.map((s) => s.id))
    const added = fresh.filter((s) => !known.has(s.id))
    setSites(fresh)
    if (added.length > 0) {
      setSiteIds((prev) => [...prev, ...added.map((s) => s.id)])
      setAutoSites(false)
      setDirty(true)
    }
    setSiteDialogOpen(false)
  }

  /**
   * Ошибки сервера по полям заявки («siteValues.<площадка>.<ключ>») — под полями на шаге
   * «Площадки»; мастер переходит туда, если подача остановилась на другом шаге.
   */
  function showFieldErrors(err: unknown): void {
    if (!(err instanceof ApiError) || !err.details) return
    const next: Record<string, string> = {}
    for (const d of err.details) {
      if (d.field.startsWith('siteValues.')) next[d.field.slice('siteValues.'.length)] = d.message
    }
    if (Object.keys(next).length === 0) return
    setFieldErrors(next)
    setStep('sites')
  }

  /** Сохранение черновика; false — ошибка (показана). */
  async function saveDraft(): Promise<boolean> {
    if (!dirty) return true
    // Отходы проверяются до отправки — как в карточке строки (код необязателен).
    const fkkoErrors: Record<string, string> = {}
    for (const id of siteIds) {
      for (const f of orderFields.filter((x) => x.type === 'fkko_list')) {
        const errs = fkkoRowErrors(siteFkko[id]?.[f.key] ?? [], { codeOptional: true })
        for (const [k, v] of Object.entries(errs)) fkkoErrors[`${id}.${f.key}.${k}`] = v
      }
    }
    if (Object.keys(fkkoErrors).length > 0) {
      setFieldErrors(fkkoErrors)
      setError('Проверьте отходы в реквизитах площадок')
      setStep('sites')
      return false
    }
    try {
      const saved = await ecologyOrdersApi.patch(order.id, {
        catalogItemId,
        siteIds,
        answers,
        comment,
        siteValues: siteValuesPayload(),
      })
      // Файлы уходят отдельными запросами и в черновик не пересылаются — их список берём из ответа
      // сервера: после смены услуги и возврата к прежней привязка на сервере сохраняется, и мастер
      // должен её показать.
      filesFrom(saved)
      setDirty(false)
      return true
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить черновик заказа'))
      showFieldErrors(err)
      return false
    }
  }

  /** Переход между шагами: несохранённое уходит в черновик на сервере. */
  async function go(next: StepKey) {
    setSaving(dirty)
    setError(null)
    const saved = await saveDraft()
    setSaving(false)
    if (saved) setStep(next)
  }

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      if (!(await saveDraft())) return
      const done = await ordersApi.submit(order.id)
      setSent(done)
      onSubmitted()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить заявку'))
      showFieldErrors(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <div ref={topRef} className="scroll-mt-4">
        <Card>
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-round bg-green-bg text-green">
              <Check className="size-5 stroke-current" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-16 font-semibold text-txt">Заявка принята</h2>
              <p className="text-txt">В ближайшее время с вами свяжется менеджер.</p>
              <p className="mt-1 text-13 text-txt-secondary">
                Заказ {sent.number} · {item?.name ?? 'Услуга'} · площадок: {siteIds.length}. Ход
                работ виден в «Моих заказах», строки услуги в разделе «Экология» помечены «На
                согласовании».
              </p>
            </div>
          </div>
        </Card>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => navigate(`/orders/${order.id}`)}>Открыть заказ</Button>
          <Button variant="secondary" onClick={() => navigate('/ecology')}>
            К разделу «Экология»
          </Button>
        </div>
      </div>
    )
  }

  const canNext =
    step === 'service' ? Boolean(catalogItemId) : step === 'sites' ? siteIds.length > 0 : true

  const commentField = (
    <Field label="Комментарий менеджеру" className={step === 'questions' ? '' : 'mt-0'}>
      {(props) => (
        <textarea
          {...props}
          rows={3}
          className={TEXTAREA_CLASS}
          value={comment}
          placeholder="Сроки, особенности, кому звонить"
          onChange={(e) => {
            setComment(e.target.value)
            setDirty(true)
          }}
        />
      )}
    </Field>
  )

  return (
    <div ref={topRef} className="scroll-mt-4">
      <Stepper steps={steps.map((k) => STEP_LABELS[k])} current={stepIndex} className="mb-3" />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-12 text-txt-tertiary">
        <span>
          Шаг {stepIndex + 1} из {steps.length} — {STEP_LABELS[step].toLowerCase()}
        </span>
        <span aria-live="polite">{saving ? 'Сохранение…' : dirty ? '' : 'Черновик сохранён'}</span>
      </div>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {step === 'service' ? (
        <div>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Input
              value={serviceQuery}
              onChange={(e) => setServiceQuery(e.target.value)}
              placeholder="Поиск услуги по названию"
              className="max-w-narrow"
              aria-label="Поиск услуги"
            />
            {serviceSection ? (
              <Button variant="secondary" onClick={() => setServiceSection(null)}>
                Показать все услуги
              </Button>
            ) : null}
          </div>
          {serviceGroups.length === 0 ? (
            <Panel>
              <EmptyState
                icon={SearchX}
                title="Услуги не найдены"
                description="Измените запрос — поиск идёт по названию и описанию услуги."
              />
            </Panel>
          ) : (
            <div className="flex flex-col gap-5">
              {serviceGroups.map((g) => (
                <section key={g.group} aria-labelledby={`eco-services-${g.group}`}>
                  <h3
                    id={`eco-services-${g.group}`}
                    className="mb-2 text-13 font-semibold uppercase tracking-wide text-txt-tertiary"
                  >
                    {ECO_SERVICE_GROUP_LABELS[g.group]}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 nw:grid-cols-2">
                    {g.items.map((c) => (
                      <ServicePickCard
                        key={c.id}
                        icon={<Leaf className="size-5 stroke-current text-accent" aria-hidden />}
                        title={c.name}
                        description={c.clientDescription || ecoTermLabel(c)}
                        selected={c.id === catalogItemId}
                        onSelect={() => chooseService(c)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          <p className="mt-3 text-12 text-txt-tertiary">
            Справочник услуг ведёт Центрика. Цены — в договоре после проверки заявки менеджером.
          </p>
        </div>
      ) : null}

      {step === 'sites' ? (
        <div className="flex flex-col gap-4">
          <Card
            header={
              <span className="flex flex-wrap items-center justify-between gap-2">
                По каким площадкам
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSiteDialogOpen(true)}
                  leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
                >
                  Добавить площадку
                </Button>
              </span>
            }
          >
            {sites.length === 0 ? (
              <p className="text-13 text-txt-secondary">
                Площадок пока нет — добавьте площадку, по которой нужна услуга.
              </p>
            ) : (
              <div className="flex flex-col gap-3" role="group" aria-label="Площадки">
                {sites.map((s) => (
                  <div key={s.id}>
                    <LabeledCheckbox
                      align="start"
                      checked={siteIds.includes(s.id)}
                      onCheckedChange={(on) => toggleSite(s.id, on)}
                    >
                      <span className="block font-medium text-txt">{s.name}</span>
                      <span className="block text-12 text-txt-secondary">
                        {[s.address, s.category ? ecoSiteCategoryLabel(s.category) : '']
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </LabeledCheckbox>
                    {/* Реквизиты площадки — под отмеченной площадкой, с отступом под подпись. */}
                    {orderFields.length > 0 && siteIds.includes(s.id) ? (
                      <div className="sm:ml-7 mt-2 border-l-2 border-line pl-4">
                        <OrderSiteFields
                          siteName={s.name}
                          fields={orderFields}
                          values={siteValues[s.id] ?? {}}
                          errors={Object.fromEntries(
                            orderFields
                              .filter((f) => fieldErrors[`${s.id}.${f.key}`])
                              .map((f) => [f.key, fieldErrors[`${s.id}.${f.key}`]]),
                          )}
                          onChange={(key, value) => changeSiteValue(s.id, key, value)}
                          fkko={siteFkko[s.id] ?? {}}
                          fkkoErrors={Object.fromEntries(
                            orderFields
                              .filter((f) => f.type === 'fkko_list')
                              .map((f) => {
                                const prefix = `${s.id}.${f.key}.`
                                return [
                                  f.key,
                                  Object.fromEntries(
                                    Object.entries(fieldErrors)
                                      .filter(([k]) => k.startsWith(prefix))
                                      .map(([k, v]) => [k.slice(prefix.length), v]),
                                  ),
                                ]
                              }),
                          )}
                          onFkko={(key, rows) => changeSiteFkko(s.id, key, rows)}
                          files={siteFiles[s.id] ?? {}}
                          filesBusy={filesBusy || saving || submitting}
                          onUploadFile={(key, file) => uploadFile(s.id, key, file)}
                          onDeleteFile={(key, fileId) => deleteFile(s.id, key, fileId)}
                        />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-12 text-txt-tertiary">
              {item?.orgWide
                ? 'По этой услуге отмечены все площадки организации — снимите лишние. '
                : ''}
              Реквизиты организации подставятся из «Настроек».
            </p>
          </Card>
          {lastStep === 'sites' ? <Card>{commentField}</Card> : null}
        </div>
      ) : null}

      {step === 'questions' ? (
        <div className="flex flex-col gap-4">
          <Alert variant="info" title="Ответы из опроса подставлены">
            Проверьте — они уйдут менеджеру вместе с заявкой. Недостающее менеджер уточнит.
          </Alert>
          <Card>
            {orderQuestions.map((q) => (
              <QuestionField
                key={q.id}
                question={q}
                answer={answers[q.id]}
                onChange={(next) => {
                  setAnswers((prev) => ({ ...prev, [q.id]: next }))
                  setDirty(true)
                }}
              />
            ))}
            {commentField}
          </Card>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        {step === 'service' ? (
          <Button variant="secondary" onClick={() => navigate('/ecology')}>
            Отмена
          </Button>
        ) : (
          <Button
            variant="secondary"
            disabled={saving || submitting}
            onClick={() => void go(steps[stepIndex - 1])}
            leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
          >
            Назад
          </Button>
        )}
        {step !== lastStep ? (
          <Button
            disabled={!canNext || saving}
            onClick={() => void go(steps[stepIndex + 1])}
            rightIcon={<ArrowRight className="size-4 stroke-current" aria-hidden />}
          >
            Далее — {STEP_LABELS[steps[stepIndex + 1]].toLowerCase()}
          </Button>
        ) : (
          <Button
            disabled={submitting || saving || !catalogItemId || siteIds.length === 0}
            onClick={() => void submit()}
            leftIcon={<Check className="size-4 stroke-current" aria-hidden />}
          >
            {submitting ? 'Отправка…' : 'Отправить заявку'}
          </Button>
        )}
      </div>

      <SiteDialog
        open={siteDialogOpen}
        site={null}
        api={ecologyClientApi}
        onClose={() => setSiteDialogOpen(false)}
        onSaved={(overview) => siteAdded(overview.sites)}
      />
    </div>
  )
}
